import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, doc, getDoc, setDoc, updateDoc, deleteField } from "firebase/firestore";
import { db } from "../lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../hooks/useAuth";

const NAVY = "#0B1F4A";
const ORANGE = "#C83803";
const ORANGE_DARK = "#7a1e00";
const NAVY_MID = "#1e3f82";
const WHITE = "#FFFFFF";

// Clear every diary's per-session passcode-unlock flag. Called on sign-out so
// that logging back in (even as the same user, in the same browser tab) re-locks
// any passcode-protected diaries. sessionStorage survives a client-side Firebase
// signOut/signIn within one tab — it's tied to the tab, not the auth session —
// so we must clear these flags explicitly rather than rely on the auth change.
// Keys are written by DiaryView as "lt_unlocked_<diaryId>".
function clearAllPasscodeUnlocks() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("lt_unlocked_")) keys.push(k);
    }
    keys.forEach(k => sessionStorage.removeItem(k));
  } catch {}
}

function generateSlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 20) || "diary";
}

function usernameFromEmail(email) {
  return (email || "").split("@")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
}

// ── THEME BOOK STYLES ─────────────────────────────────────────────────────────

const BOOK_THEMES = {
  notebook: {
    spineBg: "#8B3A0A",
    spineTextColor: "rgba(255,220,180,0.7)",
    coverBg: "#fffef5",
    pageEdgeBg: "#e8e0d4",
    nameColor: NAVY,
    nameFont: "'Lora', Georgia, serif",
    emailColor: "#8B3A0A",
    statNumColor: ORANGE,
    statTextColor: "#5a3010",
    borderColor: "rgba(139,58,10,0.2)",
    ornament: "· · ·",
    ornamentColor: "rgba(200,56,3,0.3)",
  },
  scroll: {
    spineBg: "#3d1f0a",
    spineTextColor: "#c8a87a",
    coverBg: "#d4a853",
    coverLines: true,
    coverLineColor: "rgba(100,60,10,0.1)",
    pageEdgeBg: "#c8943d",
    nameColor: "#3d1f0a",
    nameFont: "Palatino, serif",
    nameStyle: "italic",
    emailColor: "rgba(61,31,10,0.6)",
    statNumColor: "#8B1a0a",
    statTextColor: "rgba(61,31,10,0.7)",
    borderColor: "rgba(61,31,10,0.2)",
    ornament: "✦ ─── ✦ ─── ✦",
    ornamentColor: "rgba(61,31,10,0.35)",
  },
  scrapbook: {
    spineBg: "#d4720a",
    spineTextColor: "rgba(255,240,200,0.8)",
    coverBg: "#fdf3c8",
    pageEdgeBg: "#f0e8b8",
    nameColor: "#3d1f0a",
    nameFont: "'Comic Sans MS', cursive",
    emailColor: "rgba(61,31,10,0.5)",
    statNumColor: ORANGE,
    statTextColor: "rgba(61,31,10,0.65)",
    borderColor: "rgba(200,56,3,0.2)",
    tape: true,
  },
  comic: {
    spineBg: "#0B1F4A",
    spineTextColor: ORANGE,
    coverBg: "white",
    coverBorder: "2.5px solid #111",
    dots: true,
    pageEdgeBg: "#e0e0e0",
    nameColor: "#111",
    nameFont: "Impact, 'Arial Black', sans-serif",
    nameUppercase: true,
    emailColor: "#555",
    statNumColor: ORANGE,
    statTextColor: "#444",
    borderColor: "#ddd",
    comicHeader: true,
  },
  telegram: {
    spineBg: NAVY,
    spineTextColor: "#e8d88a",
    coverBg: "#e0d080",
    pageEdgeBg: "#c8b860",
    nameColor: "#1a0a00",
    nameFont: "'Special Elite', 'Courier New', monospace",
    nameUppercase: true,
    emailColor: "rgba(26,10,0,0.5)",
    statNumColor: "#5a3a00",
    statTextColor: "rgba(26,10,0,0.6)",
    borderColor: "rgba(100,80,20,0.3)",
    telegramHeader: true,
  },
  nfl: {
    spineBg: "#0B1F4A",
    spineTextColor: ORANGE,
    coverBg: "#0B1F4A",
    pageEdgeBg: "#071530",
    nameColor: ORANGE,
    nameFont: "'Oswald', 'Arial Black', sans-serif",
    nameUppercase: true,
    emailColor: "rgba(200,56,3,0.6)",
    statNumColor: ORANGE,
    statTextColor: "rgba(255,255,255,0.5)",
    borderColor: "rgba(200,56,3,0.2)",
    nflHeader: true,
  },
  ransom: {
    spineBg: "#1a1a1a",
    spineTextColor: "#ffd700",
    coverBg: "#f0ebe0",
    pageEdgeBg: "#e0dbd0",
    nameColor: "#111",
    emailColor: "#666",
    statNumColor: ORANGE,
    statTextColor: "#444",
    borderColor: "rgba(0,0,0,0.12)",
    ransomLetters: true,
    tape: true,
  },
};

function getTheme(themeId) {
  return BOOK_THEMES[themeId] || BOOK_THEMES.notebook;
}

// ── LIVE STATS HOOK ───────────────────────────────────────────────────────────

function useDiaryStats(diaryId, viewerUid, memberEntry) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!diaryId) return;
    // Compute the viewer's tag access from the member entry. If they have a
    // tag filter, only their visible entries should drive stats. Owner / legacy
    // string member = no filter, sees everything.
    const access = (() => {
      if (!memberEntry) return { tags: null, includeUntagged: true };
      if (typeof memberEntry === "string") return { tags: null, includeUntagged: true };
      if (memberEntry.role === "owner") return { tags: null, includeUntagged: true };
      return {
        tags: Array.isArray(memberEntry.tags) ? memberEntry.tags : null,
        includeUntagged: memberEntry.includeUntagged !== false,
      };
    })();
    const isVisible = (e) => {
      if (access.tags === null) return true;
      const t = e.tags || [];
      if (t.length === 0) return access.includeUntagged !== false;
      const allowed = new Set(access.tags);
      return t.every(x => allowed.has(x));
    };
    // Private entries are enforced by Firestore rules (readable only if
    // non-private OR authored by the viewer). Rules don't filter queries, so a
    // single where("diaryId"==) would fail when others' private entries exist.
    // Load via two readable queries — non-private + my own — and union by id.
    let pub = new Map();
    let mine = new Map();
    const recompute = () => {
      const merged = new Map(pub);
      for (const [k, v] of mine) merged.set(k, v);
      const entries = [...merged.values()].filter(isVisible);
      const photoCount = entries.reduce((acc, e) => acc + (e.photos?.length || 0), 0);
      let lastDate = null;
      entries.forEach(e => {
        if (!e.entryDate) return;
        const d = e.entryDate.toDate ? e.entryDate.toDate() : new Date(e.entryDate);
        if (!lastDate || d > lastDate) lastDate = d;
      });
      setStats({ count: entries.length, photoCount, lastDate });
    };
    const unsubPub = onSnapshot(
      query(collection(db, "entries"), where("diaryId", "==", diaryId), where("private", "==", false)),
      snap => { pub = new Map(snap.docs.map(d => [d.id, d.data()])); recompute(); }
    );
    const unsubMine = viewerUid
      ? onSnapshot(
          query(collection(db, "entries"), where("diaryId", "==", diaryId), where("authorId", "==", viewerUid)),
          snap => { mine = new Map(snap.docs.map(d => [d.id, d.data()])); recompute(); }
        )
      : () => {};
    return () => { unsubPub(); unsubMine(); };
  }, [diaryId, viewerUid, memberEntry]);
  return stats;
}

// ── NEW-ENTRIES HOOK ──────────────────────────────────────────────────────────
// Reads the user's lastVisited timestamp for a diary, and counts how many of the
// diary's entries are newer than that AND visible to the user given their tag
// access (if any). Excludes entries authored by the user themselves.
function useNewEntriesCount(diaryId, userUid, memberEntry) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!diaryId || !userUid) return;
    let cancelled = false;
    let lastVisitedMs = 0;
    let unsub = null;

    // Read lastVisited once on mount, then start the entries listener.
    (async () => {
      try {
        const s = await getDoc(doc(db, "users", userUid, "diaryViews", diaryId));
        if (cancelled) return;
        const lv = s.exists() ? s.data()?.lastVisited : null;
        lastVisitedMs = lv?.toMillis ? lv.toMillis() : (lv ? new Date(lv).getTime() : 0);
      } catch {}
      if (cancelled) return;
      // Compute access from the member entry (string = no filter, object = restricted)
      const access = (() => {
        if (!memberEntry) return { tags: null, includeUntagged: true };
        if (typeof memberEntry === "string") return { tags: null, includeUntagged: true };
        if (memberEntry.role === "owner") return { tags: null, includeUntagged: true };
        return {
          tags: Array.isArray(memberEntry.tags) ? memberEntry.tags : null,
          includeUntagged: memberEntry.includeUntagged !== false,
        };
      })();
      const isVisible = (e) => {
        if (access.tags === null) return true;
        const t = e.tags || [];
        if (t.length === 0) return access.includeUntagged !== false;
        const allowed = new Set(access.tags);
        return t.every(x => allowed.has(x));
      };
      // Only counts OTHER people's new entries (own are skipped below), and a
      // viewer can't read others' private entries, so the non-private query is
      // sufficient and rule-compliant. (No need to union "my own" here.)
      const q = query(
        collection(db, "entries"),
        where("diaryId", "==", diaryId),
        where("private", "==", false),
      );
      unsub = onSnapshot(q, snap => {
        if (cancelled) return;
        if (!lastVisitedMs) { setCount(0); return; } // never visited → don't show "new"
        let n = 0;
        snap.docs.forEach(d => {
          const e = d.data();
          if (e.authorId && e.authorId === userUid) return; // own entries don't count
          if (!isVisible(e)) return;
          const ts = e.createdAt?.toMillis ? e.createdAt.toMillis() : (e.createdAt ? new Date(e.createdAt).getTime() : 0);
          if (ts > lastVisitedMs) n++;
        });
        setCount(n);
      });
    })();
    return () => { cancelled = true; if (unsub) unsub(); };
  }, [diaryId, userUid, memberEntry]);
  return count;
}

// ── MEMBER PROFILES HOOK ──────────────────────────────────────────────────────

function useMemberProfiles(members) {
  const [profiles, setProfiles] = useState({});
  useEffect(() => {
    if (!members) return;
    const uids = Object.keys(members);
    let cancelled = false;
    Promise.all(uids.map(async uid => {
      try {
        const s = await getDoc(doc(db, "users", uid));
        if (s.exists()) {
          const d = s.data();
          return [uid, { name: d.name || d.email?.split("@")[0] || uid, photoURL: d.photoURL || "" }];
        }
      } catch {}
      return [uid, { name: uid, photoURL: "" }];
    })).then(results => {
      if (cancelled) return;
      setProfiles(Object.fromEntries(results));
    });
    return () => { cancelled = true; };
  }, [members]);
  return profiles;
}

// ── RANSOM LETTERS ────────────────────────────────────────────────────────────

const RANSOM_COLORS = [
  { bg: ORANGE, color: "white" }, { bg: NAVY, color: "white" },
  { bg: "#ffd700", color: "#333" }, { bg: "#222", color: "white" },
  { bg: "#2a7a3a", color: "white" }, { bg: "#8B1a0a", color: "white" },
  { bg: "#6b2d82", color: "white" }, { bg: "#1a6b8a", color: "white" },
];

function RansomName({ name }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "2px", justifyContent: "center" }}>
      {name.split("").map((ch, i) => {
        const c = RANSOM_COLORS[i % RANSOM_COLORS.length];
        return (
          <span key={i} style={{ display: "inline-block", background: c.bg, color: c.color, padding: "1px 5px", fontSize: "15px", fontWeight: "900", borderRadius: "1px", fontFamily: "sans-serif" }}>
            {ch === " " ? "\u00a0" : ch}
          </span>
        );
      })}
    </div>
  );
}

// ── DIARY BOOK COMPONENT ──────────────────────────────────────────────────────

// ── AVATAR STACK ──────────────────────────────────────────────────────────────

function AvatarStack({ members, profiles, onLight = false }) {
  const uids = Object.keys(members || {});
  if (uids.length < 2) return null; // solo diary — nothing to show
  const visible = uids.slice(0, 3);
  const overflow = uids.length - visible.length;
  const borderColor = onLight ? "white" : "rgba(255,255,255,0.9)";
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "6px", gap: 0 }}>
      {visible.map((uid, i) => {
        const p = profiles?.[uid];
        const initial = (p?.name || "?").slice(0, 1).toUpperCase();
        return p?.photoURL ? (
          <img key={uid} src={p.photoURL} alt="" referrerPolicy="no-referrer" title={p.name}
            style={{ width: "20px", height: "20px", borderRadius: "50%", border: `1.5px solid ${borderColor}`, objectFit: "cover", marginLeft: i === 0 ? 0 : "-6px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }} />
        ) : (
          <div key={uid} title={p?.name || ""}
            style={{ width: "20px", height: "20px", borderRadius: "50%", border: `1.5px solid ${borderColor}`, background: onLight ? "#e8e4dc" : "rgba(255,255,255,0.3)", color: onLight ? NAVY : "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "700", fontFamily: "-apple-system, sans-serif", marginLeft: i === 0 ? 0 : "-6px", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
            {initial}
          </div>
        );
      })}
      {overflow > 0 && (
        <div style={{ height: "20px", minWidth: "20px", padding: "0 5px", borderRadius: "10px", border: `1.5px solid ${borderColor}`, background: onLight ? "#f0ede8" : "rgba(255,255,255,0.3)", color: onLight ? "#555" : "white", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "700", fontFamily: "-apple-system, sans-serif", marginLeft: "-6px" }}>+{overflow}</div>
      )}
    </div>
  );
}

function DiaryBook({ diary, onClick, onSettings }) {
  const { user } = useAuth();
  const theme = getTheme(diary.themeId);
  const stats = useDiaryStats(diary.id, user?.uid, diary.members?.[user?.uid]);
  const memberCount = Object.keys(diary.members || {}).length;
  const memberProfiles = useMemberProfiles(diary.members);
  // Determine if this diary is "shared with you" (current viewer is not the owner)
  // and look up the owner's display name for the "Shared by X" badge.
  const myMemberEntry = diary.members?.[user?.uid];
  const myRole = (typeof myMemberEntry === "string" ? myMemberEntry : myMemberEntry?.role) || null;
  const isShared = myRole && myRole !== "owner";
  const ownerEntry = Object.entries(diary.members || {}).find(([, v]) =>
    (typeof v === "string" ? v : v?.role) === "owner"
  );
  const ownerUid = ownerEntry ? ownerEntry[0] : diary.ownerId;
  const ownerName = ownerUid ? (memberProfiles[ownerUid]?.name || "") : "";
  // Count of new entries (visible to viewer, since their lastVisited)
  const newEntriesCount = useNewEntriesCount(diary.id, user?.uid, myMemberEntry);

  const isComic = diary.themeId === "comic";
  const isTelegram = diary.themeId === "telegram";
  const isNfl = diary.themeId === "nfl";
  const isScrapbook = diary.themeId === "scrapbook";
  const isRansom = diary.themeId === "ransom";
  const isScroll = diary.themeId === "scroll";
  const hasCoverImage = !!diary.coverImageUrl;

  return (
    <div
      onClick={onClick}
      style={{ display: "flex", cursor: "pointer", position: "relative", height: "230px", transition: "transform 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-6px)"}
      onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
    >
      {/* Shadow under book */}
      <div style={{ position: "absolute", bottom: "-12px", left: "5%", right: "5%", height: "14px", background: "rgba(0,0,0,0.5)", filter: "blur(10px)", borderRadius: "50%" }} />

      {/* "Shared with you" + "X new" badges. Positioned above book so they sit on
          top of any cover and are independent of theme. Stacks vertically. */}
      {(isShared || newEntriesCount > 0) && (
        <div style={{ position: "absolute", top: "-8px", right: "-6px", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", zIndex: 10, pointerEvents: "none" }}>
          {isShared && (
            <span title={ownerName ? `Shared by ${ownerName}` : "Shared with you"}
              style={{ background: NAVY, color: "white", fontSize: "9px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", padding: "3px 8px", borderRadius: "10px", fontFamily: "-apple-system,sans-serif", boxShadow: "0 2px 6px rgba(0,0,0,0.35)", whiteSpace: "nowrap" }}>
              Shared{ownerName ? ` · ${ownerName}` : ""}
            </span>
          )}
          {newEntriesCount > 0 && (
            <span title={`${newEntriesCount} new entries since your last visit`}
              style={{ background: ORANGE, color: "white", fontSize: "10px", fontWeight: 700, padding: "3px 8px", borderRadius: "10px", fontFamily: "-apple-system,sans-serif", boxShadow: "0 2px 6px rgba(0,0,0,0.35)", whiteSpace: "nowrap" }}>
              {newEntriesCount} new
            </span>
          )}
        </div>
      )}

      {/* Per-diary settings shortcut at the top-right of the cover. stopPropagation
          so clicking the gear opens settings instead of opening the diary. The
          shared/new badges sit on the corner of the book outside the cover area
          so they don't collide with this gear. */}
      {onSettings && (
        <button onClick={ev => { ev.stopPropagation(); onSettings(); }}
          title="Diary settings"
          style={{ position: "absolute", top: "8px", right: "6px", width: "28px", height: "28px", borderRadius: "50%", border: "1px solid rgba(255,255,255,0.35)", background: "rgba(0,0,0,0.35)", color: "rgba(255,255,255,0.85)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, zIndex: 11 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      )}

      {/* Spine */}
      <div style={{ width: "24px", flexShrink: 0, background: hasCoverImage ? "#2a1a0a" : theme.spineBg, borderRadius: "2px 0 0 2px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "inset -3px 0 6px rgba(0,0,0,0.45), 2px 0 6px rgba(0,0,0,0.25)", position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 8px, rgba(0,0,0,0.07) 8px, rgba(0,0,0,0.07) 9px)", borderRadius: "2px 0 0 2px" }} />
        <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: "7px", fontWeight: "700", color: hasCoverImage ? "rgba(255,220,180,0.7)" : (theme.spineTextColor || "rgba(255,255,255,0.6)"), letterSpacing: "2px", textTransform: "uppercase", fontFamily: "Georgia, serif", position: "relative", zIndex: 1, maxHeight: "200px", overflow: "hidden" }}>
          {diary.name}
        </div>
      </div>

      {/* ── Custom cover image (replaces themed cover entirely) ── */}
      {hasCoverImage ? (
        <div style={{ flex: 1, borderRadius: "0 2px 2px 0", position: "relative", overflow: "hidden", background: "#1a1208" }}>
          <img src={diary.coverImageUrl} alt={diary.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          {/* Dark gradient so the title is readable */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 25%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.85) 100%)", pointerEvents: "none" }} />
          {/* Title + email — anchored to the TOP of the cover. The dark gradient
              behind ensures legibility regardless of the underlying photo. We
              leave space at the top-right for the settings gear. */}
          <div style={{ position: "absolute", left: "10px", right: "50px", top: "10px", color: "white" }}>
            <div style={{
              fontSize: diary.name.length > 10 ? "18px" : "24px",
              fontWeight: "700",
              fontFamily: "'Lora', Georgia, serif",
              lineHeight: 1.15,
              textShadow: "0 1px 3px rgba(0,0,0,0.8)",
              textAlign: "left",
            }}>
              {diary.name}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" }}>
              <div style={{ fontSize: "11px", fontStyle: "italic", opacity: 0.9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
                {diary.inboundEmail}
              </div>
              <button
                onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(diary.inboundEmail); e.currentTarget.textContent = "✓"; setTimeout(() => { if (e.currentTarget) e.currentTarget.textContent = "⎘"; }, 1500); }}
                title="Copy email address"
                style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "12px", color: "white", opacity: 0.8, padding: "0 2px", lineHeight: 1 }}
              >⎘</button>
            </div>
          </div>
          {/* Stats bar at bottom */}
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "6px 8px 8px", color: "white" }}>
            {stats ? (
              <>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {[
                    { val: stats.count, label: stats.count === 1 ? "entry" : "entries" },
                    { val: memberCount, label: memberCount === 1 ? "author" : "authors" },
                    ...(stats.photoCount > 0 ? [{ val: stats.photoCount, label: "photos" }] : []),
                  ].map((s, i, arr) => (
                    <div key={i} style={{ display: "flex", alignItems: "center" }}>
                      <div style={{ textAlign: "center", padding: "0 8px" }}>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: "white", fontFamily: "Georgia, serif", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>{s.val}</div>
                        <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.85)", fontFamily: "Georgia, serif", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>{s.label}</div>
                      </div>
                      {i < arr.length - 1 && <div style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.3)" }} />}
                    </div>
                  ))}
                </div>
                {stats.lastDate && (
                  <div style={{ textAlign: "center", fontSize: "10px", fontStyle: "italic", marginTop: "3px", opacity: 0.85, textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
                    Last entry {stats.lastDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                )}
                {stats.count === 0 && (
                  <div style={{ textAlign: "center", fontSize: "10px", fontStyle: "italic", marginTop: "3px", opacity: 0.85, textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
                    No entries yet
                  </div>
                )}
                <AvatarStack members={diary.members} profiles={memberProfiles} onLight={false} />
              </>
            ) : (
              <div style={{ height: "28px" }} />
            )}
          </div>
          {/* Page edges */}
          <div style={{ position: "absolute", right: "-11px", top: "2px", bottom: "2px", width: "11px", background: theme.pageEdgeBg || "#e8e0d4", borderRadius: "0 2px 2px 0", boxShadow: "3px 0 8px rgba(0,0,0,0.2)" }} />
          <div style={{ position: "absolute", right: "-19px", top: "4px", bottom: "4px", width: "9px", background: theme.pageEdgeBg || "#e8e0d4", opacity: 0.5, borderRadius: "0 2px 2px 0" }} />
        </div>
      ) : (
      /* ── Themed cover (original) ── */
      <>
      {/* Cover */}
      <div style={{
        flex: 1,
        background: theme.coverBg,
        borderRadius: "0 2px 2px 0",
        border: theme.coverBorder || "none",
        borderLeft: "none",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: isComic || isTelegram || isNfl ? "0 12px 10px" : "10px 12px 10px",
      }}>

        {/* Lined paper */}
        {theme.coverLines && (
          <div style={{ position: "absolute", inset: 0, backgroundImage: `repeating-linear-gradient(transparent, transparent 18px, ${theme.coverLineColor} 18px, ${theme.coverLineColor} 19px)`, backgroundPosition: "0 24px", pointerEvents: "none" }} />
        )}
        {/* Notebook margin line */}
        {theme.marginLine && (
          <div style={{ position: "absolute", left: "28px", top: 0, bottom: 0, width: "1px", background: "rgba(200,56,3,0.25)", pointerEvents: "none" }} />
        )}
        {/* Comic halftone dots */}
        {theme.dots && (
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(0,0,0,0.05) 1px, transparent 1px)", backgroundSize: "6px 6px", pointerEvents: "none" }} />
        )}
        {/* Scrapbook tape */}
        {isScrapbook && <>
          <div style={{ position: "absolute", top: "-5px", left: "18%", width: "44px", height: "13px", background: "rgba(200,200,170,0.55)", border: "1px solid rgba(180,180,150,0.3)", borderRadius: "1px", transform: "rotate(-2deg)" }} />
          <div style={{ position: "absolute", top: "-5px", right: "18%", width: "44px", height: "13px", background: "rgba(200,200,170,0.55)", border: "1px solid rgba(180,180,150,0.3)", borderRadius: "1px", transform: "rotate(2deg)" }} />
        </>}
        {/* Ransom tape */}
        {isRansom && (
          <div style={{ position: "absolute", top: "-5px", left: "50%", transform: "translateX(-50%) rotate(-1deg)", width: "38px", height: "11px", background: "rgba(200,200,170,0.6)", border: "1px solid rgba(180,180,150,0.3)" }} />
        )}

        {/* Theme headers */}
        {isComic && (
          <div style={{ background: ORANGE, margin: "0 -12px", padding: "3px 8px", flexShrink: 0 }}>
            <span style={{ fontSize: "7px", fontWeight: 900, color: "white", fontFamily: "Impact, sans-serif", letterSpacing: "2px", textTransform: "uppercase" }}>LifeTrackr Comics</span>
          </div>
        )}
        {isTelegram && (
          <div style={{ background: NAVY, margin: "0 -12px", padding: "3px 8px", flexShrink: 0, textAlign: "center" }}>
            <span style={{ fontSize: "7px", color: "#e8d88a", fontFamily: "Courier New, monospace", letterSpacing: "2px", textTransform: "uppercase" }}>WESTERN UNION</span>
          </div>
        )}
        {isNfl && (
          <div style={{ background: ORANGE, margin: "0 -12px", padding: "3px 8px", flexShrink: 0 }}>
            <span style={{ fontSize: "7px", fontWeight: 900, color: "white", fontFamily: "'Oswald', sans-serif", letterSpacing: "3px", textTransform: "uppercase" }}>Game Day</span>
          </div>
        )}

        {/* Title area — anchored near the top of the cover. The flex:1 div
            below it absorbs the remaining space so stats stay pinned to the
            bottom. Right padding leaves room for the top-right settings gear. */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 1, paddingTop: "12px", paddingRight: "44px" }}>
          {theme.ornament && (
            <div style={{ fontSize: "9px", color: theme.ornamentColor, letterSpacing: isScroll ? "3px" : "2px", marginBottom: "5px" }}>{theme.ornament}</div>
          )}
          {isRansom ? (
            <RansomName name={diary.name} />
          ) : (
            <div style={{
              fontSize: diary.name.length > 10 ? "18px" : "24px",
              fontWeight: "700",
              color: theme.nameColor,
              fontFamily: theme.nameFont || "Georgia, serif",
              fontStyle: theme.nameStyle || "normal",
              textTransform: theme.nameUppercase ? "uppercase" : "none",
              textAlign: "center",
              letterSpacing: isComic || isTelegram || isNfl ? "1px" : "0.3px",
              lineHeight: 1.2,
            }}>
              {diary.name}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px", maxWidth: "95%" }}>
            <div style={{ fontSize: "12px", color: theme.emailColor, fontStyle: "italic", opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "center" }}>
              {diary.inboundEmail}
            </div>
            <button
              onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(diary.inboundEmail); e.currentTarget.textContent = "✓"; setTimeout(() => { if (e.currentTarget) e.currentTarget.textContent = "⎘"; }, 1500); }}
              title="Copy email address"
              style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "13px", color: theme.emailColor, opacity: 0.7, padding: "0 2px", flexShrink: 0, lineHeight: 1 }}
            >⎘</button>
          </div>
        </div>
        {/* Spacer — pushes stats to the bottom now that title is anchored top */}
        <div style={{ flex: 1 }} />

        {/* Stats */}
        <div style={{ borderTop: `1px solid ${theme.borderColor}`, paddingTop: "7px", position: "relative", zIndex: 1 }}>
          {stats ? (
            <>
              <div style={{ display: "flex", justifyContent: "center" }}>
                {[
                  { val: stats.count, label: stats.count === 1 ? "entry" : "entries" },
                  { val: memberCount, label: memberCount === 1 ? "author" : "authors" },
                  ...(stats.photoCount > 0 ? [{ val: stats.photoCount, label: "photos" }] : []),
                ].map((s, i, arr) => (
                  <div key={i} style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ textAlign: "center", padding: "0 8px" }}>
                      <div style={{ fontSize: "16px", fontWeight: "700", color: theme.statNumColor, fontFamily: "Georgia, serif" }}>{s.val}</div>
                      <div style={{ fontSize: "11px", color: theme.statTextColor, fontFamily: "Georgia, serif" }}>{s.label}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ width: "1px", height: "24px", background: theme.borderColor }} />}
                  </div>
                ))}
              </div>
              {stats.lastDate && (
                <div style={{ textAlign: "center", fontSize: "11px", color: theme.statTextColor, fontStyle: "italic", fontFamily: "Georgia, serif", marginTop: "5px", opacity: 0.7 }}>
                  Last entry {stats.lastDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
              {stats.count === 0 && (
                <div style={{ textAlign: "center", fontSize: "11px", color: theme.statTextColor, fontStyle: "italic", fontFamily: "Georgia, serif", marginTop: "2px", opacity: 0.6 }}>
                  No entries yet
                </div>
              )}
              <AvatarStack members={diary.members} profiles={memberProfiles} onLight={true} />
            </>
          ) : (
            <div style={{ height: "40px" }} />
          )}
        </div>

        {/* Page edges */}
        <div style={{ position: "absolute", right: "-11px", top: "2px", bottom: "2px", width: "11px", background: theme.pageEdgeBg || "#e8e0d4", borderRadius: "0 2px 2px 0", boxShadow: "3px 0 8px rgba(0,0,0,0.2)" }} />
        <div style={{ position: "absolute", right: "-19px", top: "4px", bottom: "4px", width: "9px", background: theme.pageEdgeBg || "#e8e0d4", opacity: 0.5, borderRadius: "0 2px 2px 0" }} />
      </div>
      </>
      )}
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user, logout, isPro } = useAuth();
  const [billingBusy, setBillingBusy] = useState(false);
  const navigate = useNavigate();

  const [diaries, setDiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  // Reorder mode + per-user diary order (loaded from users/{uid}.diaryOrder)
  const [reorderMode, setReorderMode] = useState(false);
  const [diaryOrder, setDiaryOrder] = useState([]);
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const scrollTimerRef = useRef(null);
  // Avatar menu + Account Settings modal state
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  // Emails that never became entries. The sender is told by email the moment
  // one fails, and again in the nightly digest, but email is no good to
  // somebody who only reads it now and then (Michael, 2026-10-09: "if i don't
  // go into the app regularly ... when i go to lifetrackr will i see the 5
  // that failed and the reason?"). So they wait here until acknowledged.
  const [failedEmails, setFailedEmails] = useState([]);
  const [failedOpen, setFailedOpen] = useState(false);
  const [failedBusy, setFailedBusy] = useState(false);
  const [emailSignoff, setEmailSignoff] = useState("");
  const [signoffLoaded, setSignoffLoaded] = useState(false);
  const [signoffSaving, setSignoffSaving] = useState(false);
  const [signoffMsg, setSignoffMsg] = useState(null);
  // Passcode-reset landing: shows a confirmation banner after a reset link
  // from email is redeemed (or an error if the link is invalid/expired).
  const [passcodeResetMsg, setPasscodeResetMsg] = useState(null); // { ok, text }

  // ── Passcode reset link redemption ──
  // The email reset link lands at /?passcodeReset=<token>. When a signed-in
  // user arrives with that param, validate the token doc and, if it's valid,
  // not expired, not already used, and owned by THIS user, clear the diary's
  // passcodeHash and mark the token used. We then strip the param from the URL
  // so a refresh doesn't re-run it. Ownership is enforced by comparing the
  // token's ownerUid to the signed-in uid — the link alone can't reset someone
  // else's diary.
  useEffect(() => {
    if (!user) return;
    let token = null;
    try { token = new URLSearchParams(window.location.search).get("passcodeReset"); } catch {}
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, "passcodeResets", token);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          if (!cancelled) setPasscodeResetMsg({ ok: false, text: "That reset link is invalid or has already been used." });
          return;
        }
        const data = snap.data();
        const expired = !data.expiresAt || Date.now() > data.expiresAt;
        if (data.used || expired) {
          if (!cancelled) setPasscodeResetMsg({ ok: false, text: "That reset link has expired. Open the diary and request a new one." });
          return;
        }
        if (data.ownerUid !== user.uid) {
          // Signed in as someone other than the diary owner — refuse.
          if (!cancelled) setPasscodeResetMsg({ ok: false, text: "This reset link belongs to a different account. Sign in as the diary owner to use it." });
          return;
        }
        // Valid: clear the passcode and consume the token.
        await updateDoc(doc(db, "diaries", data.diaryId), { passcodeHash: deleteField() });
        await updateDoc(ref, { used: true, usedAt: serverTimestamp() });
        if (!cancelled) {
          setPasscodeResetMsg({ ok: true, text: `Passcode removed for "${data.diaryName || "your diary"}". Open it and set a new one if you like.` });
        }
      } catch (e) {
        if (!cancelled) setPasscodeResetMsg({ ok: false, text: "Couldn't process the reset link. Please try again." });
      } finally {
        // Strip the param so a refresh doesn't re-trigger.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("passcodeReset");
          window.history.replaceState({}, "", url.toString());
        } catch {}
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Load diaries where the user is a member. Members can be stored as either:
  //   • a legacy string ("owner" | "contributor" | "viewer"), or
  //   • a new-form object ({ role, tags?, includeUntagged? }) for tag-restricted access.
  // Firestore's `in` filter only works on exact values, so the object form would be
  // missed by a string-only filter. Fix: run TWO queries in parallel — one for each
  // form — and merge the results by doc id.
  useEffect(() => {
    if (!user) return;
    // Diaries are loaded with a single query on `memberUids` — a flat array of
    // member uids maintained alongside the rich `members` map. This replaces
    // the old two-query (string-form + object-form) union, which couldn't be
    // expressed in a way the security rules would accept: the rule proves
    // membership via "uid in members", but a query filtering on the nested
    // members.<uid>.role subfield isn't something Firestore's rule engine can
    // verify, so it was denied ("Missing or insufficient permissions") and
    // object-form members (anyone invited with a tag filter) silently saw no
    // diary. `array-contains` on memberUids matches the rule
    // (`uid in memberUids`) exactly, so the query is allowed.
    const q = query(
      collection(db, "diaries"),
      where("memberUids", "array-contains", user.uid)
    );
    return onSnapshot(q, snap => {
      const valid = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDiaries(valid);
      setLoading(false);
    }, err => {
      console.error("dashboard diaries query error", err);
      setLoading(false);
    });
  }, [user]);

  // Load the user's preferred diary order once
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await getDoc(doc(db, "users", user.uid));
        if (cancelled) return;
        const order = s.exists() && Array.isArray(s.data().diaryOrder) ? s.data().diaryOrder : [];
        setDiaryOrder(order);
      } catch (e) { console.error(e); }
      if (!cancelled) setOrderLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Diaries sorted by user's preferred order; unordered diaries (newly created or
  // legacy users without an order) appear after, sorted by createdAt desc.
  const orderedDiaries = (() => {
    const orderIdx = new Map(diaryOrder.map((id, i) => [id, i]));
    const ordered = [];
    const unordered = [];
    for (const d of diaries) {
      if (orderIdx.has(d.id)) ordered.push(d);
      else unordered.push(d);
    }
    ordered.sort((a, b) => orderIdx.get(a.id) - orderIdx.get(b.id));
    unordered.sort((a, b) => {
      const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return tb - ta;
    });
    return [...ordered, ...unordered];
  })();

  // Diaries where someone has initiated an ownership transfer TO me. Shown
  // as accept/decline banners above the diary grid so the recipient can
  // act without opening the diary first.
  const pendingTransfersForMe = orderedDiaries.filter(d =>
    d.pendingOwnerTransfer && d.pendingOwnerTransfer.toUid === user?.uid
  );

  // Accept an ownership transfer. Atomic single-doc update:
  //  - me becomes owner (string form, no extra access settings)
  //  - old owner is demoted to contributor (preserves access; future:
  //    let the original owner pick their post-transfer role at request time)
  //  - pendingOwnerTransfer is cleared
  // Doing this as one updateDoc means rules can validate the whole swap
  // in a single security check.
  const handleAcceptTransfer = async (diary) => {
    if (!user || !diary?.pendingOwnerTransfer) return;
    const fromUid = diary.pendingOwnerTransfer.fromUid;
    if (!fromUid) return;
    try {
      await updateDoc(doc(db, "diaries", diary.id), {
        ownerId: user.uid,
        [`members.${user.uid}`]: "owner",
        [`members.${fromUid}`]: "contributor",
        pendingOwnerTransfer: deleteField(),
      });
      // No local state update needed — the snapshot listener will refresh
      // this diary in `diaries`, which propagates to orderedDiaries.
    } catch (e) {
      console.error("Failed to accept ownership transfer:", e);
      alert("Couldn't accept the transfer. Please try again.");
    }
  };

  // Decline an ownership transfer. Just clears the pending field — leaves
  // diary membership untouched (the recipient stays a member with whatever
  // role they had before).
  const handleDeclineTransfer = async (diary) => {
    if (!diary?.pendingOwnerTransfer) return;
    if (!window.confirm(`Decline ownership of "${diary.name || "this diary"}"?`)) return;
    try {
      await updateDoc(doc(db, "diaries", diary.id), {
        pendingOwnerTransfer: deleteField(),
      });
    } catch (e) {
      console.error("Failed to decline ownership transfer:", e);
      alert("Couldn't decline the transfer. Please try again.");
    }
  };

  // Persist a new order to the user doc (using setDoc with merge so we don't clobber other fields)
  const persistOrder = async (newOrder) => {
    setDiaryOrder(newOrder);
    try {
      await setDoc(doc(db, "users", user.uid), { diaryOrder: newOrder }, { merge: true });
    } catch (e) { console.error(e); }
  };

  const moveDiary = (diaryId, direction) => {
    // Build the current order from the rendered ordered list (covers unordered diaries too)
    const currentIds = orderedDiaries.map(d => d.id);
    const idx = currentIds.indexOf(diaryId);
    if (idx < 0) return;
    const swapWith = idx + direction;
    if (swapWith < 0 || swapWith >= currentIds.length) return;
    const next = currentIds.slice();
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    persistOrder(next);
  };

  // Asked for once, on arrival. Read through a callable rather than straight
  // from Firestore: this repo's rules file has never reliably matched what is
  // live, so a new collection the client can read is the riskiest way in.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), "listFailedEmails");
        const res = await fn();
        if (!cancelled) setFailedEmails((res && res.data && res.data.failures) || []);
      } catch {
        // Never let this get in the way of the diaries loading.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Put one in from here, rather than going back to your mail to resend it.
  // The words were kept; photos and files were not, so say so plainly.
  const [recovering, setRecovering] = useState(null);
  const recoverFailure = async (f) => {
    setRecovering(f.id);
    try {
      const fn = httpsCallable(getFunctions(), "recoverFailedEmail");
      const res = await fn({ id: f.id });
      setFailedEmails(list => list.filter(x => x.id !== f.id));
      const left = (res && res.data && res.data.attachmentsLeft) || 0;
      window.alert(left
        ? `Logged. The ${left} attachment${left === 1 ? "" : "s"} couldn't be included — send ${left === 1 ? "it" : "them"} separately and they'll attach to a new entry.`
        : "Logged.");
    } catch (e) {
      window.alert("Couldn't log it. Try sending the email again instead.");
    }
    setRecovering(null);
  };

  const dismissFailures = async (ids) => {
    setFailedBusy(true);
    const gone = new Set(ids);
    setFailedEmails(list => list.filter(f => !gone.has(f.id)));   // feels instant
    try {
      const fn = httpsCallable(getFunctions(), "dismissFailedEmail");
      await fn({ ids });
    } catch {
      // If it didn't stick, it comes back on the next visit — which is the
      // safe direction to fail in for something whose whole job is to nag.
    }
    setFailedBusy(false);
  };

  // ── Drag-and-drop reorder (active in reorderMode only) ────────────────
  const stopAutoScroll = () => {
    if (scrollTimerRef.current) {
      clearInterval(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }
  };
  const handleDragStart = (e, diaryId) => {
    if (!reorderMode) return;
    setDragId(diaryId);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox to actually start the drag
    try { e.dataTransfer.setData("text/plain", diaryId); } catch {}
  };
  const handleDragOver = (e, diaryId) => {
    if (!reorderMode || !dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (diaryId !== dragOverId) setDragOverId(diaryId);
    // Auto-scroll the page when dragging near top/bottom edges
    const edge = 80;
    const topDist = e.clientY;
    const bottomDist = window.innerHeight - e.clientY;
    let direction = 0;
    if (topDist < edge) direction = -1;
    else if (bottomDist < edge) direction = 1;
    if (direction === 0) { stopAutoScroll(); return; }
    if (!scrollTimerRef.current) {
      scrollTimerRef.current = setInterval(() => {
        window.scrollBy(0, direction * 14);
      }, 16);
    }
  };
  const handleDrop = (e, targetId) => {
    if (!reorderMode || !dragId) { stopAutoScroll(); return; }
    e.preventDefault();
    stopAutoScroll();
    if (dragId === targetId) { setDragId(null); setDragOverId(null); return; }
    const currentIds = orderedDiaries.map(d => d.id);
    const fromIdx = currentIds.indexOf(dragId);
    const toIdx = currentIds.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); setDragOverId(null); return; }
    const next = currentIds.slice();
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, dragId);
    persistOrder(next);
    setDragId(null);
    setDragOverId(null);
  };
  const handleDragEnd = () => {
    stopAutoScroll();
    setDragId(null);
    setDragOverId(null);
  };
  // Cleanup scroll timer on unmount
  useEffect(() => () => stopAutoScroll(), []);

  // Load the user's email sign-off lazily — first time the Account modal opens.
  // Reuses the doc read from the diaryOrder load if it already happened.
  useEffect(() => {
    if (!showAccount || !user || signoffLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await getDoc(doc(db, "users", user.uid));
        if (cancelled) return;
        const v = s.exists() && typeof s.data().emailSignoff === "string" ? s.data().emailSignoff : "";
        setEmailSignoff(v);
      } catch (e) { console.error(e); }
      if (!cancelled) setSignoffLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [showAccount, user, signoffLoaded]);

  const saveEmailSignoff = async () => {
    if (!user || signoffSaving) return;
    setSignoffSaving(true);
    setSignoffMsg(null);
    try {
      await setDoc(doc(db, "users", user.uid), { emailSignoff: emailSignoff.trim() }, { merge: true });
      setSignoffMsg({ type: "ok", text: "Saved." });
      setTimeout(() => setSignoffMsg(null), 2200);
    } catch (e) {
      console.error(e);
      setSignoffMsg({ type: "err", text: "Couldn't save. Try again." });
    } finally {
      setSignoffSaving(false);
    }
  };

  // Close the avatar menu when clicking elsewhere
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      // Don't close if click was inside the menu / avatar (those have stopPropagation)
      setMenuOpen(false);
    };
    // Defer adding the listener so the click that OPENED the menu doesn't immediately close it
    const t = setTimeout(() => document.addEventListener("click", onDocClick), 0);
    return () => { clearTimeout(t); document.removeEventListener("click", onDocClick); };
  }, [menuOpen]);

  const createDiary = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    const username = usernameFromEmail(user.email);
    const diarySlug = generateSlug(newName.trim());
    const inboundEmail = `${username}.${diarySlug}@lifetrackr.app`;
    await addDoc(collection(db, "diaries"), {
      ownerId: user.uid,
      name: newName.trim(),
      slug: diarySlug,
      ownerUsername: username,
      inboundEmail,
      createdAt: serverTimestamp(),
      members: { [user.uid]: "owner" },
      memberUids: [user.uid], // flat index for membership queries/rules
      authorizedEmails: [user.email],
    });
    setNewName(""); setShowCreate(false); setCreating(false);
  };

  // Go straight to open diary — skip the cover
  const openDiary = (diaryId) => {
    navigate(`/diary/${diaryId}`, { state: { openDiary: true } });
  };

  return (
    <div style={{ minHeight: "100vh", background: NAVY, fontFamily: "Georgia, serif" }}>
      <style>{`
        @keyframes wiggle {
          0% { transform: rotate(-0.6deg); }
          100% { transform: rotate(0.6deg); }
        }
        /* Hide ↑↓ reorder arrows on desktop where drag works reliably; keep them on
           mobile (≤768px) where iOS drag is finicky and tapping is the friendlier path. */
        @media (min-width: 769px) {
          .reorder-arrows { display: none !important; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 40px", background: ORANGE, borderBottom: `3px solid ${ORANGE_DARK}` }}>
        <span style={{ fontSize: "26px", fontWeight: "900", color: WHITE, cursor: "pointer" }} onClick={() => navigate("/")}>
          Life<span style={{ color: NAVY }}>Trackr</span>
        </span>
        <div style={{ position: "relative" }}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
            title="Account menu"
            aria-label="Account menu"
            style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
            {user?.photoURL
              ? <img src={user.photoURL} alt="" style={{ width: "36px", height: "36px", borderRadius: "50%", border: `2px solid ${NAVY}`, display: "block" }} referrerPolicy="no-referrer" />
              : <div style={{ width: "36px", height: "36px", borderRadius: "50%", border: `2px solid ${NAVY}`, background: WHITE, color: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "14px", fontFamily: "Georgia, serif" }}>
                  {(user?.email || "?").slice(0, 1).toUpperCase()}
                </div>}
          </button>
          {menuOpen && (
            <div onClick={e => e.stopPropagation()}
              style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: "240px", background: WHITE, borderRadius: "10px", boxShadow: "0 8px 28px rgba(0,0,0,0.25)", zIndex: 50, overflow: "hidden", fontFamily: "-apple-system,sans-serif" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0ede8" }}>
                <div style={{ fontSize: "14px", fontWeight: 700, color: NAVY, marginBottom: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user?.displayName || usernameFromEmail(user?.email)}
                </div>
                <div style={{ fontSize: "12px", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email}</div>
                <div style={{ marginTop: "8px", display: "inline-block", fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "20px", background: isPro ? "#FFF4E5" : "#f0ede8", color: isPro ? ORANGE : "#888" }}>
                  {isPro ? "PRO" : "FREE"}
                </div>
              </div>
              {isPro ? (
                <button onClick={async () => {
                    try {
                      setBillingBusy(true);
                      const fn = httpsCallable(getFunctions(), "createPortalSession");
                      const res = await fn();
                      if (res?.data?.url) { window.location.assign(res.data.url); return; }
                      throw new Error("No portal URL");
                    } catch (e) {
                      console.error("createPortalSession failed", e);
                      setBillingBusy(false);
                      alert("Couldn't open the billing portal. Please try again.");
                    }
                  }}
                  disabled={billingBusy}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "transparent", border: "none", cursor: billingBusy ? "default" : "pointer", fontSize: "14px", color: "#222", fontFamily: "-apple-system,sans-serif" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f7f5f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {billingBusy ? "Opening…" : "Manage subscription"}
                </button>
              ) : (
                <button onClick={async () => {
                    try {
                      setBillingBusy(true);
                      const fn = httpsCallable(getFunctions(), "createCheckoutSession");
                      const res = await fn();
                      if (res?.data?.url) { window.location.assign(res.data.url); return; }
                      throw new Error("No checkout URL");
                    } catch (e) {
                      console.error("createCheckoutSession failed", e);
                      setBillingBusy(false);
                      alert("Couldn't start checkout. Please try again.");
                    }
                  }}
                  disabled={billingBusy}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "transparent", border: "none", cursor: billingBusy ? "default" : "pointer", fontSize: "14px", color: ORANGE, fontWeight: 600, fontFamily: "-apple-system,sans-serif" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f7f5f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {billingBusy ? "Opening…" : "Upgrade to Pro — $2.99/mo"}
                </button>
              )}
              <button onClick={() => { setMenuOpen(false); setShowAccount(true); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "transparent", border: "none", borderTop: "1px solid #f0ede8", cursor: "pointer", fontSize: "14px", color: "#222", fontFamily: "-apple-system,sans-serif" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f7f5f0"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Account settings
              </button>
              <button onClick={() => { setMenuOpen(false); clearAllPasscodeUnlocks(); logout(); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 16px", background: "transparent", border: "none", borderTop: "1px solid #f0ede8", cursor: "pointer", fontSize: "14px", color: "#c0392b", fontFamily: "-apple-system,sans-serif" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fdf3f1"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </nav>

      <main style={{ maxWidth: "1000px", margin: "0 auto", padding: "48px 24px" }}>

        {/* Emails that never made it in. Deliberately the first thing on the
            page and impossible to mistake for anything else — the whole point
            is that these used to disappear without a trace. */}
        {failedEmails.length > 0 && (
          <div style={{ marginBottom: "28px", background: "#FFF4E5", border: `2px solid ${ORANGE}`, borderRadius: "12px", overflow: "hidden", fontFamily: "-apple-system,sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 18px", cursor: "pointer" }}
                 onClick={() => setFailedOpen(o => !o)}>
              <span style={{ fontSize: "20px" }} aria-hidden="true">⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: ORANGE_DARK, fontSize: "15px" }}>
                  {failedEmails.length === 1
                    ? "1 email wasn't recorded"
                    : `${failedEmails.length} emails weren't recorded`}
                </div>
                <div style={{ fontSize: "13px", color: "#8a6a4a" }}>
                  They never became entries. Log one from here, or send it again
                  — photos and files have to come by email.
                </div>
              </div>
              <span style={{ fontSize: "13px", fontWeight: 700, color: ORANGE_DARK }}>
                {failedOpen ? "Hide" : "Show"}
              </span>
            </div>

            {failedOpen && (
              <div style={{ borderTop: `1px solid ${ORANGE}`, background: WHITE }}>
                {failedEmails.map(f => (
                  <div key={f.id} style={{ padding: "14px 18px", borderBottom: "1px solid #f0ede8", display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: NAVY, wordBreak: "break-word" }}>
                        {f.subject || "(no subject)"}
                      </div>
                      {f.text && (
                        <div style={{ fontSize: "13px", color: "#555", marginTop: "3px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {f.text.length > 240 ? f.text.slice(0, 240) + "…" : f.text}
                        </div>
                      )}
                      <div style={{ fontSize: "12px", color: "#999", marginTop: "5px" }}>
                        {new Date(f.when).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        {" · "}{f.reason}
                        {f.attachmentNames && f.attachmentNames.length > 0 &&
                          ` · ${f.attachmentNames.length} attachment${f.attachmentNames.length === 1 ? "" : "s"}: ${f.attachmentNames.join(", ")}`}
                      </div>
                    </div>
                    <div style={{ flex: "0 0 auto", display: "flex", gap: "6px" }}>
                      <button onClick={() => recoverFailure(f)} disabled={recovering === f.id || failedBusy}
                        title={f.attachmentNames && f.attachmentNames.length
                          ? "Files can't be included — send those separately"
                          : "Put this in as an entry"}
                        style={{ background: ORANGE, border: "none", borderRadius: "7px", padding: "6px 12px", fontSize: "12px", fontWeight: 700, color: WHITE, cursor: "pointer", opacity: recovering === f.id ? 0.6 : 1 }}>
                        {recovering === f.id ? "Logging…" : "Log it"}
                      </button>
                      <button onClick={() => dismissFailures([f.id])} disabled={failedBusy}
                        style={{ background: "transparent", border: "1px solid #ddd", borderRadius: "7px", padding: "6px 12px", fontSize: "12px", fontWeight: 600, color: "#888", cursor: "pointer" }}>
                        Done
                      </button>
                    </div>
                  </div>
                ))}
                <div style={{ padding: "12px 18px", textAlign: "right" }}>
                  <button onClick={() => dismissFailures(failedEmails.map(f => f.id))} disabled={failedBusy}
                    style={{ background: ORANGE, border: "none", borderRadius: "8px", padding: "8px 16px", fontSize: "13px", fontWeight: 700, color: WHITE, cursor: "pointer" }}>
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "48px" }}>
          <div>
            <h1 style={{ fontSize: "32px", fontWeight: "700", margin: "0 0 6px", color: WHITE, letterSpacing: "-0.5px" }}>Your Diaries</h1>
            <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.35)", margin: 0, fontStyle: "italic" }}>
              Your life, one entry at a time…
              <br />
              Memories, receipts, and everything in between.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)}
            title="New diary"
            aria-label="New diary"
            style={{ background: ORANGE, color: WHITE, border: "none", width: "48px", height: "48px", borderRadius: "50%", cursor: "pointer", fontSize: "28px", fontWeight: "300", fontFamily: "Georgia, serif", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, padding: 0, boxShadow: "0 4px 12px rgba(200,56,3,0.4)", transition: "transform 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
            +
          </button>
        </div>

        {/* Shelf background */}
        <div style={{ background: "linear-gradient(180deg, #3d0e00 0%, #2a0800 55%, #1a0400 100%)", borderRadius: "14px", borderTop: "3px solid rgba(200,56,3,0.6)", borderLeft: "1px solid rgba(200,56,3,0.15)", borderRight: "1px solid rgba(200,56,3,0.15)", borderBottom: "1px solid rgba(0,0,0,0.5)", padding: "56px 52px 72px", position: "relative", overflow: "hidden" }}>
          {/* Shelf wood grain */}
          <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.018) 50px, rgba(255,255,255,0.018) 51px)", pointerEvents: "none", borderRadius: "14px" }} />
          {/* Orange ambient glow at top */}
          <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: "65%", height: "80px", background: "radial-gradient(ellipse, rgba(200,56,3,0.25) 0%, transparent 70%)", pointerEvents: "none" }} />
          {/* Bottom shadow */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "14px", background: "linear-gradient(180deg, rgba(200,56,3,0.08), rgba(0,0,0,0.3))", borderRadius: "0 0 14px 14px" }} />

          {/* Reorder button — top-right, faded by default. Brightens on hover and when active. */}
          {diaries.length > 1 && (
            <button onClick={() => setReorderMode(m => !m)}
              title={reorderMode ? "Done reordering" : "Reorder diaries"}
              aria-label={reorderMode ? "Done reordering" : "Reorder diaries"}
              style={{ position: "absolute", top: "14px", right: "16px", zIndex: 4, width: "32px", height: "32px", borderRadius: "50%", background: reorderMode ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.05)", border: `1px solid ${reorderMode ? "rgba(255,255,255,0.95)" : "rgba(255,220,180,0.18)"}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, opacity: reorderMode ? 1 : 0.5, transition: "all 0.2s" }}
              onMouseEnter={e => { if (!reorderMode) e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={e => { if (!reorderMode) e.currentTarget.style.opacity = "0.5"; }}>
              {reorderMode ? (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8 L7 12 L13 4" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3 L9 15 M5 7 L9 3 L13 7 M5 11 L9 15 L13 11" stroke="rgba(255,220,180,0.85)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          )}
        {passcodeResetMsg && (
          <div style={{ maxWidth: "560px", margin: "0 auto 20px", padding: "12px 16px", borderRadius: "10px", display: "flex", alignItems: "center", gap: "10px", background: passcodeResetMsg.ok ? "rgba(40,160,80,0.15)" : "rgba(200,60,30,0.15)", border: `1px solid ${passcodeResetMsg.ok ? "rgba(40,160,80,0.5)" : "rgba(200,60,30,0.5)"}` }}>
            <span style={{ fontSize: "18px" }}>{passcodeResetMsg.ok ? "🔓" : "⚠️"}</span>
            <span style={{ color: "rgba(255,255,255,0.92)", fontSize: "13px", lineHeight: 1.5, flex: 1 }}>{passcodeResetMsg.text}</span>
            <button onClick={() => setPasscodeResetMsg(null)}
              style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.6)", fontSize: "18px", cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
        )}
        {loading ? (
          <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: "60px" }}>Loading...</p>
        ) : diaries.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <div style={{ fontSize: "56px", marginBottom: "20px" }}>📖</div>
            <h2 style={{ fontSize: "22px", fontWeight: "700", margin: "0 0 10px", color: WHITE }}>No diaries yet</h2>
            <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.4)", margin: "0 0 28px" }}>Create your first diary to start capturing memories.</p>
            <button onClick={() => setShowCreate(true)} style={{ background: ORANGE, color: WHITE, border: "none", padding: "13px 28px", borderRadius: "6px", fontSize: "15px", fontWeight: "700", cursor: "pointer", fontFamily: "Georgia, serif" }}>
              Create a diary
            </button>
          </div>
        ) : (
          <>
            {/* Pending-ownership-transfer banners. One per diary where someone
                has initiated a transfer to me. Accept performs the atomic
                role swap; Decline just clears the request. */}
            {pendingTransfersForMe.length > 0 && (
              <div style={{ marginBottom: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
                {pendingTransfersForMe.map(d => (
                  <div key={d.id} style={{ padding: "14px 18px", background: "#fff5ec", borderRadius: "10px", border: `1px solid ${ORANGE}`, display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: "200px" }}>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: ORANGE, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Ownership transfer</div>
                      <div style={{ fontSize: "14px", color: "#333", fontFamily: "-apple-system,sans-serif", lineHeight: 1.5 }}>
                        <strong>{d.pendingOwnerTransfer.fromName || "Someone"}</strong>
                        {" "}wants to transfer ownership of{" "}
                        <strong>{d.pendingOwnerTransfer.diaryName || d.name || "a diary"}</strong>
                        {" "}to you.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                      <button onClick={() => handleDeclineTransfer(d)}
                        style={{ background: "white", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 16px", fontSize: "13px", color: "#555", cursor: "pointer", fontFamily: "-apple-system,sans-serif", fontWeight: "600" }}>
                        Decline
                      </button>
                      <button onClick={() => handleAcceptTransfer(d)}
                        style={{ background: ORANGE, color: "white", border: "none", borderRadius: "6px", padding: "8px 18px", fontSize: "13px", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                        Accept
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "48px 28px", paddingBottom: "24px" }}>
            {orderedDiaries.map((diary, i) => {
              const isDragging = dragId === diary.id;
              const isDragOver = dragOverId === diary.id && dragId !== diary.id;
              return (
                <div key={diary.id}
                  draggable={reorderMode}
                  onDragStart={e => handleDragStart(e, diary.id)}
                  onDragOver={e => handleDragOver(e, diary.id)}
                  onDrop={e => handleDrop(e, diary.id)}
                  onDragEnd={handleDragEnd}
                  onDragLeave={() => { if (dragOverId === diary.id) setDragOverId(null); }}
                  style={{
                    paddingBottom: "20px", position: "relative",
                    animation: reorderMode && !isDragging ? "wiggle 0.6s ease-in-out infinite alternate" : "none",
                    cursor: reorderMode ? (isDragging ? "grabbing" : "grab") : "default",
                    opacity: isDragging ? 0.4 : 1,
                    transform: isDragOver ? "scale(1.04)" : "scale(1)",
                    transition: "transform 0.15s, opacity 0.15s",
                  }}>
                  {/* Drop-target glow */}
                  {isDragOver && (
                    <div style={{ position: "absolute", inset: "-8px -8px 12px -8px", border: `2px dashed ${ORANGE}`, borderRadius: "8px", background: "rgba(200,56,3,0.08)", pointerEvents: "none", zIndex: 1 }} />
                  )}
                  <DiaryBook diary={diary}
                    onClick={reorderMode ? () => {} : () => openDiary(diary.id)}
                    onSettings={reorderMode ? null : () => navigate(`/diary/${diary.id}`, { state: { openSettings: true } })} />
                  {reorderMode && (
                    <div className="reorder-arrows" style={{ position: "absolute", top: "-10px", right: "-6px", display: "flex", flexDirection: "column", gap: "4px", zIndex: 5 }}>
                      <button onClick={(e) => { e.stopPropagation(); moveDiary(diary.id, -1); }}
                        disabled={i === 0}
                        title="Move up"
                        aria-label="Move diary up"
                        style={{ width: "28px", height: "28px", borderRadius: "50%", border: "none", background: i === 0 ? "rgba(255,255,255,0.15)" : ORANGE, color: WHITE, cursor: i === 0 ? "default" : "pointer", fontSize: "14px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: i === 0 ? "none" : "0 2px 6px rgba(0,0,0,0.4)", lineHeight: 1, padding: 0 }}>↑</button>
                      <button onClick={(e) => { e.stopPropagation(); moveDiary(diary.id, 1); }}
                        disabled={i === orderedDiaries.length - 1}
                        title="Move down"
                        aria-label="Move diary down"
                        style={{ width: "28px", height: "28px", borderRadius: "50%", border: "none", background: i === orderedDiaries.length - 1 ? "rgba(255,255,255,0.15)" : ORANGE, color: WHITE, cursor: i === orderedDiaries.length - 1 ? "default" : "pointer", fontSize: "14px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: i === orderedDiaries.length - 1 ? "none" : "0 2px 6px rgba(0,0,0,0.4)", lineHeight: 1, padding: 0 }}>↓</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
        </div>
      </main>

      {/* CREATE MODAL */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={() => setShowCreate(false)}>
          <div style={{ background: WHITE, borderRadius: "16px", padding: "40px", width: "100%", maxWidth: "440px", margin: "0 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: "22px", fontWeight: "700", margin: "0 0 10px", color: NAVY }}>Create a new diary</h2>
            <p style={{ fontSize: "14px", color: "#999", margin: "0 0 24px", lineHeight: 1.6 }}>Name it after a person, a family, or a chapter of your life.</p>
            <input
              style={{ width: "100%", background: "#f8f8f8", border: "1px solid #ddd", borderRadius: "10px", padding: "13px 16px", color: "#222", fontSize: "15px", fontFamily: "Georgia, serif", outline: "none", boxSizing: "border-box", marginBottom: "12px" }}
              type="text"
              placeholder="e.g. Lucas, Our Family, 2026..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && createDiary()}
              autoFocus
            />
            {newName.trim() && (
              <div style={{ background: "rgba(200,56,3,0.06)", border: "1px solid rgba(200,56,3,0.15)", borderRadius: "8px", padding: "10px 14px", marginBottom: "20px" }}>
                <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Email address to log memories</div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: ORANGE, fontFamily: "Georgia, serif", wordBreak: "break-all" }}>
                  {usernameFromEmail(user?.email)}.{generateSlug(newName.trim())}@lifetrackr.app
                </div>
                <div style={{ fontSize: "11px", color: "#aaa", marginTop: "4px" }}>Anyone you authorize can email this address to log a memory.</div>
              </div>
            )}
            {!newName.trim() && <div style={{ marginBottom: "20px" }} />}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button onClick={() => setShowCreate(false)} style={{ background: "transparent", border: "1px solid #ddd", color: "#888", padding: "11px 20px", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontFamily: "Georgia, serif" }}>Cancel</button>
              <button onClick={createDiary} disabled={creating} style={{ background: ORANGE, color: WHITE, border: "none", padding: "11px 24px", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "Georgia, serif", opacity: creating ? 0.6 : 1 }}>
                {creating ? "Creating..." : "Create diary"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACCOUNT SETTINGS MODAL */}
      {showAccount && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={() => setShowAccount(false)}>
          <div style={{ background: WHITE, borderRadius: "16px", padding: "36px 36px 32px", width: "100%", maxWidth: "480px", margin: "0 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", fontFamily: "-apple-system,sans-serif" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", margin: "0 0 6px", color: NAVY, fontFamily: "Georgia, serif" }}>Account settings</h2>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 24px", lineHeight: 1.5 }}>Settings that apply to your account across all diaries.</p>

            {/* Email sign-off */}
            <div style={{ background: "#fafaf7", border: "1px solid #ece8e0", borderRadius: "8px", padding: "14px 16px", marginBottom: "20px" }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: NAVY, marginBottom: "4px" }}>Your email sign-off</div>
              <div style={{ fontSize: "12px", color: "#888", marginBottom: "10px", lineHeight: 1.5 }}>
                When you email an entry to a diary, we'll stop capturing at this phrase — anything below it is discarded. Leave blank to skip. Example: <span style={{ color: "#555", fontStyle: "italic" }}>Best, Michael</span>
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  type="text"
                  value={emailSignoff}
                  onChange={e => setEmailSignoff(e.target.value)}
                  placeholder="e.g. Best, Michael"
                  disabled={!signoffLoaded}
                  style={{ flex: 1, padding: "8px 10px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px", outline: "none", background: signoffLoaded ? "white" : "#f5f5f5" }}
                />
                <button onClick={saveEmailSignoff} disabled={signoffSaving || !signoffLoaded}
                  style={{ padding: "8px 14px", border: "none", borderRadius: "6px", background: ORANGE, color: "white", fontSize: "13px", fontWeight: 600, cursor: signoffSaving ? "default" : "pointer", opacity: signoffSaving || !signoffLoaded ? 0.6 : 1 }}>
                  {signoffSaving ? "Saving..." : "Save"}
                </button>
              </div>
              {signoffMsg && (
                <div style={{ marginTop: "8px", fontSize: "12px", color: signoffMsg.type === "ok" ? "#1a7a3e" : "#c0392b" }}>
                  {signoffMsg.text}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowAccount(false)} style={{ background: "transparent", border: "1px solid #ddd", color: "#666", padding: "9px 22px", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


